import { useState } from "react";
import clsx from "clsx";
import BottomSheet from "../../../../components/common/bottom-sheet";
import Header from "../../../../components/common/header";
import { BsCheckCircleFill } from "react-icons/bs";

// ─── 인증 카드 상태 타입 ───────────────────────────────────────────────────────

interface CardState {
  label: string;
  tossPaymentsSellerId: string | null;
  sellerInfoValid: boolean;
  tossSellerStatus: string | null;
}

const CARD_STATES: CardState[] = [
  {
    label: "① 정산정보 미완성 (카드 숨김)",
    tossPaymentsSellerId: null,
    sellerInfoValid: false,
    tossSellerStatus: null,
  },
  {
    label: "② 정산정보 완성 + 미등록 → 인증 시작하기",
    tossPaymentsSellerId: null,
    sellerInfoValid: true,
    tossSellerStatus: null,
  },
  {
    label: "③ APPROVAL_REQUIRED → 본인인증 대기 중",
    tossPaymentsSellerId: "mock-seller-id",
    sellerInfoValid: true,
    tossSellerStatus: "APPROVAL_REQUIRED",
  },
  {
    label: "④ PARTIALLY_APPROVED → 본인인증 완료",
    tossPaymentsSellerId: "mock-seller-id",
    sellerInfoValid: true,
    tossSellerStatus: "PARTIALLY_APPROVED",
  },
  {
    label: "⑤ APPROVED → 인증 완료",
    tossPaymentsSellerId: "mock-seller-id",
    sellerInfoValid: true,
    tossSellerStatus: "APPROVED",
  },
  {
    label: "⑥ KYC_REQUIRED → 추가 서류 필요",
    tossPaymentsSellerId: "mock-seller-id",
    sellerInfoValid: true,
    tossSellerStatus: "KYC_REQUIRED",
  },
];

const TOSS_STATUS_LABEL: Record<string, { text: string; className: string }> = {
  APPROVAL_REQUIRED: {
    text: "본인인증 대기 중",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  PARTIALLY_APPROVED: {
    text: "본인인증 완료",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  KYC_REQUIRED: {
    text: "추가 서류 확인 필요",
    className: "bg-orange-100 text-orange-800 border-orange-200",
  },
  APPROVED: {
    text: "인증 완료",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
};

// ─── 인증 카드 컴포넌트 (banking/page.tsx의 카드 로직과 동일) ──────────────────

function TossVerificationCard({
  state,
  onInitiate,
  onResend,
}: {
  state: CardState;
  onInitiate: () => void;
  onResend: () => void;
}) {
  const { tossPaymentsSellerId, sellerInfoValid, tossSellerStatus } = state;
  const hasTossId = Boolean(tossPaymentsSellerId);
  const statusInfo = tossSellerStatus ? TOSS_STATUS_LABEL[tossSellerStatus] : null;

  if (!hasTossId && sellerInfoValid) {
    return (
      <div className="w-full p-4 rounded-lg shadow-md border border-blue-200 bg-blue-50">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 opacity-70 mb-1">
          토스페이먼츠 인증
        </p>
        <p className="text-base font-bold text-blue-800 mb-3">
          본인인증이 필요해요
        </p>
        <button
          type="button"
          onClick={onInitiate}
          className="w-full rounded-lg bg-blue-500 text-white px-4 py-3 text-sm font-semibold"
        >
          인증 시작하기
        </button>
      </div>
    );
  }

  if (!statusInfo) {
    return (
      <div className="w-full p-4 rounded-lg border border-dashed border-gray-300 text-center text-gray-400 text-sm">
        카드 미표시 (정산정보 미완성)
      </div>
    );
  }

  const needsResend = tossSellerStatus === "APPROVAL_REQUIRED";
  const needsKyc = tossSellerStatus === "KYC_REQUIRED";
  const isCompleted =
    tossSellerStatus === "PARTIALLY_APPROVED" || tossSellerStatus === "APPROVED";

  return (
    <div
      className={clsx(
        "w-full p-4 rounded-lg shadow-md border",
        statusInfo.className
      )}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">
            토스페이먼츠 인증
          </p>
          <p className="text-base font-bold">{statusInfo.text}</p>
          {needsKyc && (
            <p className="text-xs mt-1 opacity-80">
              이메일을 확인하고 추가 서류를 제출해 주세요.
            </p>
          )}
        </div>
        {isCompleted && (
          <BsCheckCircleFill className="shrink-0 ml-4 text-3xl opacity-80" />
        )}
        {needsResend && (
          <button
            type="button"
            onClick={onResend}
            className="ml-4 shrink-0 rounded-lg bg-white bg-opacity-60 border border-current px-3 py-2 text-sm font-semibold"
          >
            문자 재발송
          </button>
        )}
      </div>
    </div>
  );
}

// ─── 바텀시트 상태 타입 ───────────────────────────────────────────────────────

interface SheetState {
  label: string;
  eyebrow?: string;
  mainText: string;
  subText: string;
  buttonText: string;
}

const SHEET_STATES: SheetState[] = [
  {
    label: "최초 저장 후 (settlement-info-update)",
    eyebrow: "최초 1회 인증이 필요해요",
    mainText: "📩 인증 문자가 발송됐어요",
    subText:
      "등록된 번호로 토스페이먼츠 인증 문자를 보내드렸어요.\n문자의 링크를 눌러 인증을 완료해 주세요.",
    buttonText: "확인",
  },
  {
    label: "정산센터 인증 시작하기 후",
    eyebrow: "최초 1회 인증이 필요해요",
    mainText: "📩 인증 문자가 발송됐어요",
    subText:
      "등록된 번호로 토스페이먼츠 인증 문자를 보내드렸어요.\n문자의 링크를 눌러 인증을 완료해 주세요.",
    buttonText: "확인",
  },
];

// ─── 프리뷰 페이지 ────────────────────────────────────────────────────────────

export default function TossCardPreview() {
  const [openSheetIndex, setOpenSheetIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header title="[DEV] 토스 인증 UI 프리뷰" showBackButton />

      <div className="max-w-xl mx-auto px-4 pb-12 space-y-8 pt-4">

        {/* 인증 카드 상태 */}
        <section>
          <h2 className="text-lg font-bold text-gray-700 mb-3">
            인증 카드 상태별 렌더링
          </h2>
          <div className="space-y-4">
            {CARD_STATES.map((state, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs text-gray-500 font-medium">{state.label}</p>
                <TossVerificationCard
                  state={state}
                  onInitiate={() => setOpenSheetIndex(0)}
                  onResend={() => alert("[mock] 인증 문자 재발송")}
                />
              </div>
            ))}
          </div>
        </section>

        {/* 바텀시트 트리거 */}
        <section>
          <h2 className="text-lg font-bold text-gray-700 mb-3">
            바텀시트 상태별 미리보기
          </h2>
          <div className="space-y-3">
            {SHEET_STATES.map((sheet, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs text-gray-500 font-medium">{sheet.label}</p>
                <button
                  type="button"
                  onClick={() => setOpenSheetIndex(i)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  바텀시트 열기
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* 바텀시트 */}
      {SHEET_STATES.map((sheet, i) => (
        <BottomSheet
          key={i}
          isOpen={openSheetIndex === i}
          onClose={() => setOpenSheetIndex(null)}
          eyebrow={sheet.eyebrow}
          mainText={sheet.mainText}
          subText={sheet.subText}
          buttonText={sheet.buttonText}
          onButtonClick={() => setOpenSheetIndex(null)}
          disableDrag
        />
      ))}
    </div>
  );
}
